import { useEffect, useRef, useState } from "react";
import { Pencil, Upload, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

/**
 * SignaturePad
 *
 * Permite dibujar una firma con mouse/touch o subir una imagen.
 * Devuelve una data URL PNG con fondo transparente vía onSave(dataUrl).
 *
 * Props:
 *  - initialValue: string | null (data URL existente)
 *  - onSave(dataUrl: string): se llama al pulsar "Guardar"
 *  - onClear(): se llama al pulsar "Eliminar firma"
 *  - onCancel(): se llama al pulsar "Cancelar"
 */
export default function SignaturePad({ initialValue, onSave, onClear, onCancel }) {
  const [mode, setMode] = useState("draw"); // 'draw' | 'upload'
  const [hasDrawn, setHasDrawn] = useState(false);
  const [uploadedDataUrl, setUploadedDataUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);

  // Setup canvas con DPI alto y fondo transparente
  useEffect(() => {
    if (mode !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = "#0F2A1F";
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }, [mode]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPt.current = getPos(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
    if (!hasDrawn) setHasDrawn(true);
  };
  const end = (e) => {
    e?.preventDefault?.();
    drawing.current = false;
  };

  const clearCanvas = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  // Convierte el canvas (sin fondo) a PNG con bbox recortado
  const exportFromCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    const data = ctx.getImageData(0, 0, width, height).data;
    let minX = width, minY = height, maxX = 0, maxY = 0, found = false;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const a = data[(y * width + x) * 4 + 3];
        if (a > 8) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
          found = true;
        }
      }
    }
    if (!found) return null;
    const pad = 8;
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad);
    maxY = Math.min(height - 1, maxY + pad);
    const w = maxX - minX + 1;
    const h = maxY - minY + 1;
    const out = document.createElement("canvas");
    out.width = w;
    out.height = h;
    const octx = out.getContext("2d");
    octx.drawImage(canvas, minX, minY, w, h, 0, 0, w, h);
    return out.toDataURL("image/png");
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Sube un archivo de imagen (PNG, JPG)");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("La imagen no debe superar 4 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Re-render a un canvas máximo 800x300 para reducir peso
        const maxW = 800, maxH = 300;
        const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        setUploadedDataUrl(c.toDataURL("image/png"));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    let dataUrl = null;
    if (mode === "draw") {
      if (!hasDrawn) { toast.error("Dibuja tu firma primero"); return; }
      dataUrl = exportFromCanvas();
    } else {
      if (!uploadedDataUrl) { toast.error("Sube una imagen primero"); return; }
      dataUrl = uploadedDataUrl;
    }
    if (!dataUrl) { toast.error("No se pudo procesar la firma"); return; }
    if (dataUrl.length > 800_000) {
      toast.error("La firma es demasiado grande. Reduce el tamaño y vuelve a intentarlo.");
      return;
    }
    setSaving(true);
    try { await onSave(dataUrl); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="signature-pad">
      {initialValue && (
        <div className="border border-border rounded-sm p-3 bg-muted/20">
          <div className="label-eyebrow mb-2">Firma actual</div>
          <div className="bg-white rounded-sm p-3 inline-block max-w-full">
            <img src={initialValue} alt="Firma actual" className="max-h-24" />
          </div>
        </div>
      )}

      <div className="flex border-b border-border">
        <button
          onClick={() => setMode("draw")}
          data-testid="sig-mode-draw"
          className={`px-4 h-10 text-sm font-semibold inline-flex items-center gap-2 border-b-2 -mb-px ${
            mode === "draw" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Pencil className="w-4 h-4" /> Dibujar
        </button>
        <button
          onClick={() => setMode("upload")}
          data-testid="sig-mode-upload"
          className={`px-4 h-10 text-sm font-semibold inline-flex items-center gap-2 border-b-2 -mb-px ${
            mode === "upload" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Upload className="w-4 h-4" /> Subir imagen
        </button>
      </div>

      {mode === "draw" ? (
        <div className="space-y-2">
          <div className="bg-white rounded-sm border-2 border-dashed border-border" style={{ touchAction: "none" }}>
            <canvas
              ref={canvasRef}
              className="block w-full h-48 cursor-crosshair"
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={end}
              onMouseLeave={end}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={end}
              data-testid="sig-canvas"
            />
          </div>
          <div className="flex justify-between items-center">
            <p className="text-[11px] text-muted-foreground">Firma con el mouse o el dedo dentro del recuadro.</p>
            <button onClick={clearCanvas} className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1" data-testid="sig-clear-canvas">
              <Trash2 className="w-3 h-3" /> Limpiar
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block border-2 border-dashed border-border rounded-sm p-6 text-center cursor-pointer hover:bg-muted/30 transition" data-testid="sig-upload-label">
            <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
            <div className="text-sm font-semibold">Click para subir tu firma</div>
            <div className="text-xs text-muted-foreground mt-1">PNG, JPG · idealmente fondo blanco</div>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="sig-upload-input" />
          </label>
          {uploadedDataUrl && (
            <div className="bg-white rounded-sm border border-border p-3 inline-block max-w-full">
              <img src={uploadedDataUrl} alt="Vista previa" className="max-h-24" />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
          data-testid="sig-save-btn"
        >
          <Check className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar firma"}
        </button>
        {initialValue && (
          <button
            onClick={onClear}
            className="px-4 h-10 rounded-sm border border-destructive/40 text-destructive hover:bg-destructive/10 text-sm inline-flex items-center gap-2"
            data-testid="sig-delete-btn"
          >
            <Trash2 className="w-4 h-4" /> Eliminar firma
          </button>
        )}
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-4 h-10 rounded-sm border border-border hover:bg-muted text-sm inline-flex items-center gap-2 ml-auto"
            data-testid="sig-cancel-btn"
          >
            <X className="w-4 h-4" /> Cancelar
          </button>
        )}
      </div>
    </div>
  );
}
